// =============================================================================
// Impersonation service.
//
// Wraps the `POST /api/Account/ImpersonateUser` endpoint plus the local
// state-juggling needed to swap the current session for the target user's
// without losing the admin's original token. Stop-impersonation is purely
// client-side — discard the impersonation token and restore the saved
// admin one.
//
// State model
//   * The admin's *original* authToken + userProfile are snapshotted into
//     sessionStorage under `originalAuthToken` / `originalUserProfile` at
//     impersonation start.
//   * `isImpersonating` flag in sessionStorage drives the red banner and
//     gates the menu items that don't make sense while impersonating.
//   * On Stop, we wipe the impersonation token, restore the snapshot, then
//     trigger a full app reload so Redux + permission caches rehydrate from
//     the admin's identity.
// =============================================================================

import axiosInstance from "../data/interface/axiosInstance";
import { getBaseUrl } from "../data/interface/getBaseUrl";

const baseURL = getBaseUrl(true);

const KEYS = {
  // Snapshots of the admin's real session, restored on Stop.
  originalAuthToken: "originalAuthToken",
  originalUserProfile: "originalUserProfile",
  originalRememberMe: "originalRememberMe",
  // Snapshot of the admin's Redux permissionsState — needed because that
  // store persists to localStorage and would otherwise leak admin-level
  // module access into the impersonated session.
  originalPermissionsState: "originalPermissionsState",
  // Display-time metadata for the banner.
  impersonating: "isImpersonating",
  impersonatedUserName: "impersonatedUserName",
  impersonatedUserEmail: "impersonatedUserEmail",
} as const;

/**
 * Mirrors `login.tsx`'s rights → permissionsState transformation. The
 * impersonated user's `rights[]` payload is identical in shape to login's,
 * so we can reuse the same dedup-by-moduleId reduce step. We write directly
 * to localStorage because the caller hard-reloads right after — Redux
 * dispatches wouldn't survive the reload, but `permissionsSlice` reads its
 * initial state from `localStorage.permissionsState`.
 */
const writePermissionsState = (rights: any[] = [], permissionKeys: string[] = [], enabledModules?: string[]) => {
  // NB: `rights` is no longer emitted by the backend (V2 cutover) — it may be
  // undefined. We must still persist permissionKeys, so don't bail out here.
  const safeRights = Array.isArray(rights) ? rights : [];
  const uniqueModules = safeRights.reduce<any[]>((acc, curr) => {
    if (!acc.find((m) => m.id === curr.moduleId)) {
      acc.push({
        id: curr.moduleId,
        name: curr.moduleName,
        addedBy: curr.addedBy || "",
        addedDate: curr.addedDate || "",
        updatedBy: curr.updatedBy || null,
        updatedDate: curr.updatedDate || null,
        inActive: false,
        inActiveReason: null,
        inActiveBy: null,
        inActiveDate: null,
      });
    }
    return acc;
  }, []);
  localStorage.setItem(
    "permissionsState",
    // Permissions V2 — carry the impersonated user's keys so KEY-FIRST gating
    // reflects THEIR access, not the admin's, after the reload.
    JSON.stringify({
      modules: uniqueModules,
      permissions: safeRights,
      permissionKeys: Array.isArray(permissionKeys) ? permissionKeys : [],
      enabledModules: Array.isArray(enabledModules) ? enabledModules : null,
    })
  );
};

// login.tsx always persists authToken to localStorage regardless of the
// "Remember Me" checkbox (it only gates the remembered-email convenience),
// so that's the only place the admin's real token can be found.
const pickAuthStorage = (): Storage => localStorage;

export interface ImpersonateApiResponse {
  isError: boolean;
  responseMessage?: string;
  dataModel?: {
    userId: number;
    email: string;
    name: string;
    token: string;
    permissionKeys: string[];
    enabledModules?: string[];
    roles: any[];
    isImpersonating: boolean;
    impersonatedByUserId: number;
    impersonatedByEmail: string;
  };
}

/**
 * Calls the backend, stashes the admin's current session, then swaps in the
 * new token + profile. Returns the API payload so the caller can do anything
 * special with the rights/roles before redirecting.
 */
export const startImpersonation = async (
  targetUserId: number,
  targetDisplayName: string,
  targetEmail: string
): Promise<ImpersonateApiResponse["dataModel"]> => {
  const { data } = await axiosInstance.post<ImpersonateApiResponse>(
    `${baseURL}/Account/ImpersonateUser?targetUserId=${encodeURIComponent(targetUserId)}`
  );

  if (data.isError || !data.dataModel) {
    throw new Error(data.responseMessage || "Impersonation request failed.");
  }

  const storage = pickAuthStorage();

  // 1. Snapshot the admin's current session BEFORE we overwrite anything.
  const adminToken = storage.getItem("authToken");
  const adminProfile = storage.getItem("userProfile");
  const adminPermissions = localStorage.getItem("permissionsState");
  if (!adminToken) {
    throw new Error("Admin session token not found — refusing to impersonate.");
  }
  // Snapshots intentionally live in sessionStorage even if remember-me was on:
  // they shouldn't survive a browser restart. Closing the tab = exit
  // impersonation safely on the admin's side.
  sessionStorage.setItem(KEYS.originalAuthToken, adminToken);
  if (adminProfile) sessionStorage.setItem(KEYS.originalUserProfile, adminProfile);
  if (adminPermissions) sessionStorage.setItem(KEYS.originalPermissionsState, adminPermissions);
  sessionStorage.setItem(KEYS.originalRememberMe, storage === localStorage ? "true" : "false");

  // 2. Swap in the impersonation token + a fresh userProfile.
  storage.setItem("authToken", data.dataModel.token);
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${data.dataModel.token}`;
  storage.setItem(
    "userProfile",
    JSON.stringify({
      userId: data.dataModel.userId,
      name: data.dataModel.name,
      email: data.dataModel.email,
      roles: data.dataModel.roles || [],
      // Carry the flag inside userProfile too, so any code path that reads
      // userProfile (e.g. Redux hydration) can detect impersonation.
      isImpersonating: true,
      impersonatedByUserId: data.dataModel.impersonatedByUserId,
      impersonatedByEmail: data.dataModel.impersonatedByEmail,
    })
  );
  storage.setItem("userid", String(data.dataModel.userId));

  // 3. Replace the persisted Redux permissions store with the impersonated
  //    user's rights so screens render only the modules they actually have
  //    access to. The hard-reload below picks this up via permissionsSlice's
  //    `loadState()` initial state.
  // Permissions V2 — only the key list matters now (legacy `rights` retired).
  writePermissionsState([], data.dataModel.permissionKeys || [], data.dataModel.enabledModules);

  // 4. Banner-visible metadata.
  sessionStorage.setItem(KEYS.impersonating, "true");
  sessionStorage.setItem(KEYS.impersonatedUserName, targetDisplayName || data.dataModel.name);
  sessionStorage.setItem(KEYS.impersonatedUserEmail, targetEmail || data.dataModel.email);

  return data.dataModel;
};

/**
 * Restores the snapshotted admin session and forces a full reload so every
 * Redux slice, permission cache, and route guard re-evaluates against the
 * admin's identity. Returns true if a stop happened, false if the user
 * wasn't impersonating in the first place.
 */
export const stopImpersonation = (): boolean => {
  if (!isImpersonating()) return false;

  const originalToken = sessionStorage.getItem(KEYS.originalAuthToken);
  const originalProfile = sessionStorage.getItem(KEYS.originalUserProfile);
  const originalRemember = sessionStorage.getItem(KEYS.originalRememberMe) === "true";
  if (!originalToken) {
    // Snapshot lost — safest is to log the user out completely.
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem(KEYS.impersonating);
    window.location.replace("/login");
    return true;
  }

  const target = originalRemember ? localStorage : sessionStorage;
  const other = originalRemember ? sessionStorage : localStorage;

  // Restore the admin's token + profile to whichever store they were in.
  target.setItem("authToken", originalToken);
  other.removeItem("authToken");
  if (originalProfile) {
    target.setItem("userProfile", originalProfile);
    other.removeItem("userProfile");
    try {
      const parsed = JSON.parse(originalProfile);
      if (parsed?.userId != null) target.setItem("userid", String(parsed.userId));
    } catch {
      /* non-fatal — userid is recoverable from /me endpoints */
    }
  }
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${originalToken}`;

  // Restore the admin's Redux permissions store so reload boots with the
  // admin's modules instead of the impersonated user's narrower ones.
  const originalPermissions = sessionStorage.getItem(KEYS.originalPermissionsState);
  if (originalPermissions) {
    localStorage.setItem("permissionsState", originalPermissions);
  } else {
    // No snapshot — safer to wipe so admin sees a re-fetch flow than to
    // carry over the impersonated user's stripped permissions.
    localStorage.removeItem("permissionsState");
  }

  // Wipe impersonation snapshots — they're one-shot.
  sessionStorage.removeItem(KEYS.originalAuthToken);
  sessionStorage.removeItem(KEYS.originalUserProfile);
  sessionStorage.removeItem(KEYS.originalPermissionsState);
  sessionStorage.removeItem(KEYS.originalRememberMe);
  sessionStorage.removeItem(KEYS.impersonating);
  sessionStorage.removeItem(KEYS.impersonatedUserName);
  sessionStorage.removeItem(KEYS.impersonatedUserEmail);

  // Hard reload so Redux, permissions, query cache, and routing all
  // re-bootstrap against the admin's restored token.
  window.location.replace("/");
  return true;
};

export const isImpersonating = (): boolean =>
  sessionStorage.getItem(KEYS.impersonating) === "true";

export const getImpersonationBannerData = () => ({
  isImpersonating: isImpersonating(),
  userName: sessionStorage.getItem(KEYS.impersonatedUserName) || "",
  userEmail: sessionStorage.getItem(KEYS.impersonatedUserEmail) || "",
});
